import { SignableDocument } from "app/models/document-params";
import { MasterTradeParams } from "app/models/master-trade-params";
import { getMergedDocument, getSubTradeSignableDocument, signDocument } from "app/state/ducks/signable-document/thunks";
import { ErrorNotification, SuccessNotification } from "app/views/components/notifications/notification";

export const onSignDocument = async (
    signBy: "buyer" | "seller" | "both",
    signableDocument: SignableDocument | null,
    dispatch: any,
) => {
    if (!signableDocument) {
        ErrorNotification({
            title: 'Error',
            message: 'No document to sign.',
        });
        return;
    }

    if (signBy === 'both') {
        const buyerSignResponse = await handleSingleSignDocument('buyer', signableDocument, dispatch);
        if (!buyerSignResponse) return;

        const sellerSignResponse = await handleSingleSignDocument('seller', signableDocument, dispatch);
        if (!sellerSignResponse) return;

        SuccessNotification({
            title: 'Success',
            message: 'Document signed successfully by both parties.',
        });
        return sellerSignResponse;
    }
    else {
        const signResponse = await handleSingleSignDocument(signBy, signableDocument, dispatch);
        if (!signResponse) return;

        SuccessNotification({
            title: 'Success',
            message: `Document signed successfully by ${signBy}.`,
        });
        return signResponse;
    }
}

const handleSingleSignDocument = async (signBy: "buyer" | "seller", signableDocument: SignableDocument, dispatch: any) => {
    if (!signableDocument) {
        ErrorNotification({
            title: 'Error',
            message: 'No document to sign.',
        });
        return;
    }

    try {
        // Assuming you have a function to handle the signing process
        const signableDocumentResponse: any = await dispatch(signDocument(signableDocument, signBy, {}))
        if (!signableDocumentResponse || !signableDocumentResponse.success) {
            ErrorNotification({
                title: 'Error',
                message: signableDocumentResponse?.payload || `Failed to sign the document as ${signBy}.`,
            });
            return;
        }
        // DocuSeal merges the new signature into the final PDF asynchronously,
        // so the URL returned by the sign endpoint is usually null. Wait briefly
        // and re-fetch a fresh URL with the merged document.
        const signedDocumentType = signableDocumentResponse.payload.documentType;
        if (signedDocumentType === 'AssignmentAgreementDocument' || signedDocumentType === 'ConfirmationDocument') {
            await new Promise(resolve => setTimeout(resolve, 500));

            const refreshedResponse: any = signedDocumentType === 'AssignmentAgreementDocument'
                ? await getMergedDocument(
                    signableDocumentResponse.payload.contractId,
                    signedDocumentType
                )
                : await getSubTradeSignableDocument(
                    signableDocumentResponse.payload.contractId,
                    signedDocumentType
                );

            if (refreshedResponse && refreshedResponse.success) {
                return refreshedResponse.payload;
            } else {
                console.error(`Failed to fetch refreshed ${signedDocumentType}`);
                return signableDocumentResponse.payload;
            }
        }

        // For other documents, return the sign response directly
        return signableDocumentResponse.payload;
    } catch {
        ErrorNotification({
            title: 'Error',
            message: 'Failed to sign the document.',
        });
        return
    }
}

export const getPossibleActions = (masterTrade: MasterTradeParams, signableDocument: SignableDocument | null) => {
    if (!signableDocument) return [];

    // Get parties who signed online via DocuSeal
    const partiesWhoSigned = signableDocument.signatures.map(sig => sig.signerType.toLowerCase())

    // Get parties who signed offline (admin signed for them with uploaded documents)
    const adminSignedForParties = signableDocument.adminSignedFor?.map(
        adminSigned => adminSigned.signerPartyInfo.signerType.toLowerCase()
    ) || [];

    // Combine both online and offline signatures
    const allSignedParties = [...partiesWhoSigned, ...adminSignedForParties];

    const actions = [];
    const buyerSigned = allSignedParties.includes('buyer');
    const sellerSigned = allSignedParties.includes('seller');

    if (!buyerSigned) {
        actions.push('buyer');
    }
    if (!sellerSigned) {
        actions.push('seller');
    }
    // Only allow "both" if neither buyer nor seller has signed yet
    if (!buyerSigned && !sellerSigned) {
        actions.push('both');
    }
    return actions;
}
